import { useState, useMemo, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
	UsersIcon,
	ClockIcon,
	MagnifyingGlassIcon,
	PlusIcon,
	PencilSquareIcon,
	EyeIcon,
	EnvelopeIcon,
	PhoneIcon,
	UserIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import { Button, Badge, Modal, Drawer, Spinner, Autocomplete } from '../../components/ui';
import useProviderProfileStore from '../../stores/providerProfileStore';
import staffApi from '../../api/staff';
import type { StaffMember, StaffFormData } from '../../types/staff';

// ---- Constants ----
const AVATAR_COLORS = [
	'#667eea',
	'#764ba2',
	'#f59e0b',
	'#10b981',
	'#ec4899',
	'#3b82f6',
	'#8b5cf6',
	'#f97316',
	'#06b6d4',
	'#14b8a6',
	'#a855f7',
	'#84cc16',
];

const getAvatarColor = (id: number) => AVATAR_COLORS[id % AVATAR_COLORS.length];

const emptyFormData: StaffFormData = {
	firstName: '',
	lastName: '',
	email: '',
	phone: '',
	role: 'BRAIDER',
	status: 'ACTIVE',
	staffType: 'PERMANENT',
	staffGrade: 'ENTRY',
	hireDate: new Date().toISOString().split('T')[0],
	commissionRate: 0,
};

const staffMemberToFormData = (s: StaffMember): StaffFormData => ({
	firstName: s.firstName,
	lastName: s.lastName,
	email: s.email,
	phone: s.phone,
	role: s.role,
	status: s.status,
	staffType: s.staffType,
	staffGrade: s.staffGrade,
	hireDate: s.hireDate,
	commissionRate: s.commissionRate,
});

// ---- Helpers ----
const getInitials = (first: string, last: string) =>
	`${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

const getStatusBadge = (staff: StaffMember) => {
	switch (staff.status) {
		case 'ACTIVE':
			return <Badge variant="success">{staff.statusName}</Badge>;
		case 'ON_LEAVE':
			return <Badge variant="warning">{staff.statusName}</Badge>;
		case 'INACTIVE':
			return <Badge variant="danger">{staff.statusName}</Badge>;
		case 'TERMINATED':
			return <Badge variant="danger">{staff.statusName}</Badge>;
		default:
			return <Badge variant="info">{staff.statusName}</Badge>;
	}
};

const getRoleBadge = (staff: StaffMember) => {
	switch (staff.role) {
		case 'BRAIDER':
			return <Badge variant="primary">{staff.roleName}</Badge>;
		case 'STYLIST':
			return <Badge variant="info">{staff.roleName}</Badge>;
		case 'MANAGER':
			return <Badge variant="warning">{staff.roleName}</Badge>;
		case 'RECEPTIONIST':
			return <Badge variant="danger">{staff.roleName}</Badge>;
		default:
			return <Badge variant="info">{staff.roleName}</Badge>;
	}
};

// ---- Sub-components ----

// Stats Grid
const StatsGrid = ({ staff }: { staff: StaffMember[] }) => {
	const totalStaff = staff.length;
	const activeStaff = staff.filter(s => s.status === 'ACTIVE').length;
	const stylistsBraiders = staff.filter(s => s.role === 'STYLIST' || s.role === 'BRAIDER').length;
	const onLeave = staff.filter(s => s.status === 'ON_LEAVE').length;

	const stats = [
		{
			label: 'Total Staff',
			value: totalStaff,
			icon: UsersIcon,
			colorClass: 'staff-stat-card--purple',
		},
		{
			label: 'Active',
			value: activeStaff,
			icon: CheckCircleIcon,
			colorClass: 'staff-stat-card--green',
		},
		{
			label: 'Stylists / Braiders',
			value: stylistsBraiders,
			icon: UserIcon,
			colorClass: 'staff-stat-card--blue',
		},
		{
			label: 'On Leave',
			value: onLeave,
			icon: ClockIcon,
			colorClass: 'staff-stat-card--orange',
		},
	];

	return (
		<div className="staff-stats">
			{stats.map(stat => (
				<div key={stat.label} className={`staff-stat-card ${stat.colorClass}`}>
					<div className="staff-stat-card__icon">
						<stat.icon className="w-6 h-6" />
					</div>
					<div className="staff-stat-card__content">
						<span className="staff-stat-card__value">{stat.value}</span>
						<span className="staff-stat-card__label">{stat.label}</span>
					</div>
				</div>
			))}
		</div>
	);
};

// Staff Detail Drawer
interface StaffDetailDrawerProps {
	staff: StaffMember | null;
	isOpen: boolean;
	onClose: () => void;
	onEdit: (staff: StaffMember) => void;
}

const StaffDetailDrawer = ({ staff, isOpen, onClose, onEdit }: StaffDetailDrawerProps) => {
	return (
		<Drawer
			isOpen={isOpen}
			onClose={onClose}
			title="Staff Details"
			width="md"
			footer={
				staff && (
					<div className="flex gap-3">
						<Button
							variant="primary"
							className="flex-1"
							onClick={() => {
								onClose();
								onEdit(staff);
							}}
						>
							<PencilSquareIcon className="w-4 h-4" />
							Edit Staff
						</Button>
						<Button variant="secondary" className="flex-1" onClick={onClose}>
							Close
						</Button>
					</div>
				)
			}
		>
			{staff && (
				<div className="staff-drawer">
					{/* Avatar + Name */}
					<div className="staff-drawer__header">
						<div
							className="staff-drawer__avatar"
							style={{ background: getAvatarColor(staff.id) }}
						>
							{getInitials(staff.firstName, staff.lastName)}
						</div>
						<div className="staff-drawer__name-block">
							<h3 className="staff-drawer__name">
								{staff.firstName} {staff.lastName}
							</h3>
							<div className="flex items-center gap-2 mt-1">
								{getRoleBadge(staff)}
								{getStatusBadge(staff)}
							</div>
						</div>
					</div>

					{/* Contact Info */}
					<div className="staff-drawer__section">
						<h4 className="staff-drawer__section-title">Contact Information</h4>
						<div className="staff-drawer__detail-row">
							<EnvelopeIcon className="w-4 h-4 text-gray-400 shrink-0" />
							<span>{staff.email}</span>
						</div>
						<div className="staff-drawer__detail-row">
							<PhoneIcon className="w-4 h-4 text-gray-400 shrink-0" />
							<span>{staff.phone}</span>
						</div>
					</div>

					{/* Employment Details */}
					<div className="staff-drawer__section">
						<h4 className="staff-drawer__section-title">Employment Details</h4>
						<div className="staff-drawer__grid">
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Staff Type</span>
								<span className="staff-drawer__field-value">
									{staff.staffTypeName}
								</span>
							</div>
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Hire Date</span>
								<span className="staff-drawer__field-value">
									{new Date(staff.hireDate).toLocaleDateString('en-US', {
										month: 'short',
										day: 'numeric',
										year: 'numeric',
									})}
								</span>
							</div>
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Grade</span>
								<span className="staff-drawer__field-value">
									{staff.staffGradeName}
								</span>
							</div>
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Commission Rate</span>
								<span className="staff-drawer__field-value">
									{staff.commissionRate}%
								</span>
							</div>
						</div>
					</div>
				</div>
			)}
		</Drawer>
	);
};

// Add/Edit Staff Modal
interface AddEditStaffModalProps {
	isOpen: boolean;
	onClose: () => void;
	staff: StaffMember | null;
	onSave: (data: StaffFormData, id?: number) => void;
	isSaving: boolean;
}

const AddEditStaffModal = ({
	isOpen,
	onClose,
	staff,
	onSave,
	isSaving,
}: AddEditStaffModalProps) => {
	const isEditing = !!staff;
	const [formData, setFormData] = useState<StaffFormData>(emptyFormData);

	// Reset form when modal opens with new staff
	useState(() => {
		if (staff) {
			setFormData(staffMemberToFormData(staff));
		} else {
			setFormData({ ...emptyFormData });
		}
	});

	const updateField = <K extends keyof StaffFormData>(key: K, value: StaffFormData[K]) => {
		setFormData(prev => ({ ...prev, [key]: value }));
	};

	const handleSearchStaff = useCallback(async (query: string): Promise<StaffMember[]> => {
		return staffApi.searchStaff(query);
	}, []);

	const handleStaffSearchSelect = useCallback((selected: StaffMember) => {
		setFormData(staffMemberToFormData(selected));
	}, []);

	const handleSearchClear = useCallback(() => {
		setFormData({ ...emptyFormData });
	}, []);

	const handleSubmit = () => {
		if (
			!formData.firstName.trim() ||
			!formData.lastName.trim() ||
			!formData.email.trim() ||
			!formData.phone.trim()
		) {
			toast.error('Please fill in all required fields.');
			return;
		}
		onSave(formData, staff?.id);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={isEditing ? 'Edit Staff Member' : 'Add Staff Member'}
			size="xl"
			bodyClassName="max-h-[70vh] overflow-y-auto"
		>
			<div className="space-y-5">
				{/* Staff Search - Add mode only */}
				{!isEditing && (
					<div className="pb-4 mb-1 border-b border-gray-200 dark:border-gray-700">
						<Autocomplete<StaffMember>
							label="Search Staff by Phone or Email"
							helperText="Search for an existing staff member to pre-fill the form"
							placeholder="Enter name, phone, or email to search..."
							onSearch={handleSearchStaff}
							onSelect={handleStaffSearchSelect}
							onClear={handleSearchClear}
							renderItem={s => (
								<div className="flex items-center gap-3">
									<div
										className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
										style={{ background: getAvatarColor(s.id) }}
									>
										{getInitials(s.firstName, s.lastName)}
									</div>
									<div className="flex-1 min-w-0">
										<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
											{s.firstName} {s.lastName}
										</div>
										<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
											{s.email} &middot; {s.phone}
										</div>
									</div>
								</div>
							)}
							getItemKey={s => s.id}
							minChars={3}
						/>
					</div>
				)}

				{/* Name Row */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							First Name <span className="provider-form__required">*</span>
						</label>
						<input
							type="text"
							className="provider-form__input"
							value={formData.firstName}
							onChange={e => updateField('firstName', e.target.value)}
							placeholder="First name"
						/>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Last Name <span className="provider-form__required">*</span>
						</label>
						<input
							type="text"
							className="provider-form__input"
							value={formData.lastName}
							onChange={e => updateField('lastName', e.target.value)}
							placeholder="Last name"
						/>
					</div>
				</div>

				{/* Contact Row */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Email <span className="provider-form__required">*</span>
						</label>
						<input
							type="email"
							className="provider-form__input"
							value={formData.email}
							onChange={e => updateField('email', e.target.value)}
							placeholder="email@example.com"
						/>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Phone <span className="provider-form__required">*</span>
						</label>
						<input
							type="tel"
							className="provider-form__input"
							value={formData.phone}
							onChange={e => updateField('phone', e.target.value)}
							placeholder="(555) 123-4567"
						/>
					</div>
				</div>

				{/* Role + Status */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Role <span className="provider-form__required">*</span>
						</label>
						<select
							className="provider-form__input"
							value={formData.role}
							onChange={e => updateField('role', e.target.value)}
						>
							<option value="BRAIDER">Braider</option>
							<option value="STYLIST">Stylist</option>
							<option value="MANAGER">Manager</option>
							<option value="RECEPTIONIST">Receptionist</option>
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Status</label>
						<select
							className="provider-form__input"
							value={formData.status}
							onChange={e => updateField('status', e.target.value)}
						>
							<option value="ACTIVE">Active</option>
							<option value="ON_LEAVE">On Leave</option>
							<option value="INACTIVE">Inactive</option>
							<option value="TERMINATED">Terminated</option>
						</select>
					</div>
				</div>

				{/* Staff Type + Hire Date */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Staff Type</label>
						<select
							className="provider-form__input"
							value={formData.staffType}
							onChange={e => updateField('staffType', e.target.value)}
						>
							<option value="PERMANENT">Permanent</option>
							<option value="ON_CALL">On Call</option>
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Hire Date</label>
						<input
							type="date"
							className="provider-form__input"
							value={formData.hireDate}
							onChange={e => updateField('hireDate', e.target.value)}
						/>
					</div>
				</div>

				{/* Grade + Commission */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Grade</label>
						<select
							className="provider-form__input"
							value={formData.staffGrade}
							onChange={e => updateField('staffGrade', e.target.value)}
						>
							<option value="ENTRY">Entry</option>
							<option value="EXPERT">Expert (+$5)</option>
							<option value="PRO">Pro (+$7)</option>
							<option value="MASTER">Master (+$10)</option>
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Commission Rate (%)</label>
						<input
							type="number"
							className="provider-form__input"
							min={0}
							max={100}
							value={formData.commissionRate}
							onChange={e => updateField('commissionRate', Number(e.target.value))}
						/>
					</div>
				</div>

				{/* Actions */}
				<div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
					<Button
						variant="secondary"
						className="flex-1"
						onClick={onClose}
						disabled={isSaving}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						className="flex-1"
						onClick={handleSubmit}
						disabled={isSaving}
					>
						{isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Staff Member'}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

// ---- Main StaffManagement Page ----
const StaffManagement = () => {
	const { selectedLocationId } = useProviderProfileStore();
	const [staffList, setStaffList] = useState<StaffMember[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [roleFilter, setRoleFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	// Fetch staff list
	const fetchStaffList = useCallback(async (locationId: number) => {
		setIsLoading(true);
		setError(null);
		try {
			const data = await staffApi.listStaff(locationId);
			setStaffList(data);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to load staff';
			setError(message);
			setStaffList([]);
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Watch selectedLocationId and refetch
	useEffect(() => {
		if (selectedLocationId) {
			fetchStaffList(selectedLocationId);
		}
	}, [selectedLocationId, fetchStaffList]);

	// Filtered staff (local filtering)
	const filteredStaff = useMemo(() => {
		return staffList.filter(staff => {
			const matchesSearch =
				!searchQuery ||
				`${staff.firstName} ${staff.lastName}`
					.toLowerCase()
					.includes(searchQuery.toLowerCase()) ||
				staff.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
				staff.phone.includes(searchQuery);

			const matchesRole = !roleFilter || staff.role === roleFilter;
			const matchesStatus = !statusFilter || staff.status === statusFilter;

			return matchesSearch && matchesRole && matchesStatus;
		});
	}, [staffList, searchQuery, roleFilter, statusFilter]);

	const handleViewStaff = (staff: StaffMember) => {
		setSelectedStaff(staff);
		setIsDrawerOpen(true);
	};

	const handleEditStaff = (staff: StaffMember) => {
		setEditingStaff(staff);
		setIsModalOpen(true);
	};

	const handleAddStaff = () => {
		setEditingStaff(null);
		setIsModalOpen(true);
	};

	const handleSaveStaff = async (data: StaffFormData, id?: number) => {
		if (!selectedLocationId) return;
		setIsSaving(true);
		try {
			if (id) {
				await staffApi.updateStaff(id, selectedLocationId, data);
				toast.success('Staff member updated successfully!');
			} else {
				await staffApi.createStaff(selectedLocationId, data);
				toast.success('Staff member added successfully!');
			}
			setIsModalOpen(false);
			setEditingStaff(null);
			fetchStaffList(selectedLocationId);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to save staff member';
			toast.error(message);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
				<div>
					<div className="flex items-center gap-3 mb-1">
						<UsersIcon className="w-6 h-6 text-gray-400" />
						<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
							Staff Management
						</h1>
					</div>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						Manage your team members, roles, and assignments
					</p>
				</div>
				<Button variant="primary" onClick={handleAddStaff}>
					<PlusIcon className="w-5 h-5" />
					Add Staff Member
				</Button>
			</div>

			{/* Loading State */}
			{isLoading && (
				<div className="py-16 text-center">
					<Spinner size="lg" className="mx-auto mb-3 text-purple-600" />
					<p className="text-sm text-gray-500 dark:text-gray-400">Loading staff...</p>
				</div>
			)}

			{/* Error State */}
			{!isLoading && error && (
				<div className="py-16 text-center">
					<ExclamationTriangleIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
					<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
						Failed to load staff
					</h3>
					<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{error}</p>
					{selectedLocationId && (
						<Button
							variant="primary"
							onClick={() => fetchStaffList(selectedLocationId)}
						>
							Try Again
						</Button>
					)}
				</div>
			)}

			{/* Content */}
			{!isLoading && !error && (
				<>
					{/* Stats Grid */}
					<StatsGrid staff={filteredStaff} />

					{/* Filter Bar */}
					<div className="settings-card mb-6">
						<div className="settings-card__body">
							<div className="staff-filters">
								<div className="staff-filters__selects">
									<select
										className="provider-form__input"
										value={roleFilter}
										onChange={e => setRoleFilter(e.target.value)}
									>
										<option value="">All Roles</option>
										<option value="BRAIDER">Braider</option>
										<option value="STYLIST">Stylist</option>
										<option value="MANAGER">Manager</option>
										<option value="RECEPTIONIST">Receptionist</option>
									</select>
									<select
										className="provider-form__input"
										value={statusFilter}
										onChange={e => setStatusFilter(e.target.value)}
									>
										<option value="">All Status</option>
										<option value="ACTIVE">Active</option>
										<option value="ON_LEAVE">On Leave</option>
										<option value="INACTIVE">Inactive</option>
										<option value="TERMINATED">Terminated</option>
									</select>
								</div>
								<div className="staff-filters__search">
									<MagnifyingGlassIcon className="staff-filters__search-icon" />
									<input
										type="text"
										className="provider-form__input staff-filters__search-input"
										placeholder="Search staff..."
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Staff Table (Desktop) */}
					<div className="staff-table-wrapper hidden md:block">
						<div className="settings-card">
							<div className="overflow-x-auto">
								<table className="staff-table">
									<thead>
										<tr>
											<th>Staff Member</th>
											<th>Status</th>
											<th>Grade</th>
											<th>Type</th>
											<th className="text-right">Actions</th>
										</tr>
									</thead>
									<tbody>
										{filteredStaff.length === 0 ? (
											<tr>
												<td
													colSpan={5}
													className="text-center py-10 text-gray-400"
												>
													No staff members found matching your filters.
												</td>
											</tr>
										) : (
											filteredStaff.map(staff => (
												<tr
													key={staff.id}
													className="staff-table__row"
													onClick={() => handleViewStaff(staff)}
												>
													<td>
														<div className="flex items-center gap-3">
															<div
																className="staff-table__avatar"
																style={{
																	background: getAvatarColor(
																		staff.id
																	),
																}}
															>
																{getInitials(
																	staff.firstName,
																	staff.lastName
																)}
															</div>
															<div>
																<div className="font-medium text-gray-900 dark:text-gray-100">
																	{staff.firstName}{' '}
																	{staff.lastName}
																</div>
																<div className="text-xs text-gray-500 dark:text-gray-400">
																	{staff.roleName} ·{' '}
																	{staff.staffGradeName}
																</div>
															</div>
														</div>
													</td>
													<td>{getStatusBadge(staff)}</td>
													<td>
														<span className="text-sm text-gray-700 dark:text-gray-300">
															{staff.staffGradeName}
														</span>
													</td>
													<td>
														<span className="text-sm text-gray-700 dark:text-gray-300">
															{staff.staffTypeName}
														</span>
													</td>
													<td className="text-right">
														<div className="flex items-center justify-end gap-1">
															<button
																type="button"
																className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400 transition-all cursor-pointer"
																onClick={e => {
																	e.stopPropagation();
																	handleViewStaff(staff);
																}}
																title="View details"
															>
																<EyeIcon className="w-4 h-4" />
															</button>
															<button
																type="button"
																className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400 transition-all cursor-pointer"
																onClick={e => {
																	e.stopPropagation();
																	handleEditStaff(staff);
																}}
																title="Edit"
															>
																<PencilSquareIcon className="w-4 h-4" />
															</button>
														</div>
													</td>
												</tr>
											))
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>

					{/* Staff Cards (Mobile) */}
					<div className="staff-cards md:hidden">
						{filteredStaff.length === 0 ? (
							<div className="settings-card">
								<div className="settings-card__body text-center py-10 text-gray-400">
									No staff members found matching your filters.
								</div>
							</div>
						) : (
							filteredStaff.map(staff => (
								<div
									key={staff.id}
									className="staff-card"
									onClick={() => handleViewStaff(staff)}
								>
									<div className="staff-card__header">
										<div
											className="staff-card__avatar"
											style={{ background: getAvatarColor(staff.id) }}
										>
											{getInitials(staff.firstName, staff.lastName)}
										</div>
										<div className="staff-card__info">
											<h4 className="staff-card__name">
												{staff.firstName} {staff.lastName}
											</h4>
											<div className="flex items-center gap-2">
												{getRoleBadge(staff)}
											</div>
										</div>
										{getStatusBadge(staff)}
									</div>
									<div className="staff-card__body">
										<div className="staff-card__field">
											<span className="staff-card__field-label">Grade</span>
											<span className="staff-card__field-value">
												{staff.staffGradeName}
											</span>
										</div>
										<div className="staff-card__field">
											<span className="staff-card__field-label">Type</span>
											<span className="staff-card__field-value">
												{staff.staffTypeName}
											</span>
										</div>
									</div>
								</div>
							))
						)}
					</div>
				</>
			)}

			{/* Staff Detail Drawer */}
			<StaffDetailDrawer
				staff={selectedStaff}
				isOpen={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				onEdit={handleEditStaff}
			/>

			{/* Add/Edit Modal */}
			<AddEditStaffModal
				key={editingStaff?.id ?? 'add'}
				isOpen={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setEditingStaff(null);
				}}
				staff={editingStaff}
				onSave={handleSaveStaff}
				isSaving={isSaving}
			/>
		</AppLayout>
	);
};

export default StaffManagement;
